import { ArrowOutward } from '@mui/icons-material';
import {
  Box,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SummaryItem } from '~/components/MyWorkspace/SummaryItem';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { AbsenceEntry, AbsenceType } from '~/data/absences/types';
import { useMenu } from '~/hooks/useMenu';
import { AbsenceStatusLabel } from '~/scenes/MyWorkspace/components/Widgets/components/MyAbsencesWidget/components/Absence/AbsenceStatusLabel';

import { useSxStyles } from './useStyles';

export interface Props {
  listItem: AbsenceEntry;
  isTabDisabled?: boolean;
  listItemMenuContent?: ReactNode;
  isLinkEnabled?: boolean;
}

export const Absence: React.FC<Props> = ({
  listItem,
  isTabDisabled,
  listItemMenuContent,
  isLinkEnabled,
}) => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const loggedInUserId = useLoggedInEmployeeId();

  const formattedStartDate = moment(listItem.startDate).format('L');
  const formattedEndDate =
    listItem.endDate && moment(listItem.endDate).format('L');

  const formattedDate = listItem.endDate
    ? `${formattedStartDate} - ${formattedEndDate} ${
        listItem.type === AbsenceType.SHORT && listItem?.days
          ? `, ${listItem?.days} ${t('days').toLowerCase()}`
          : ''
      }`
    : t(
        `${
          moment(listItem.startDate).isBefore(moment())
            ? 'startedOn'
            : 'startsOn'
        }`,
        { date: formattedStartDate },
      );

  const { open, isOpen, close, anchorEl } = useMenu();

  const showAbsenceLink = !isTabDisabled && isLinkEnabled;

  return (
    <SummaryItem
      primaryElement={
        <Box sx={sxStyles.flex}>
          <TooltippedTruncatedText>
            {listItem.reason?.name ?? ''}
          </TooltippedTruncatedText>
          <Box sx={sxStyles.flexCenter}>
            <AbsenceStatusLabel status={listItem.status} />
          </Box>
          <Menu
            open={isOpen}
            onClose={close}
            anchorEl={anchorEl}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {listItemMenuContent}
            {showAbsenceLink && !!listItemMenuContent && <Divider />}
            {showAbsenceLink && (
              <MenuItem
                component={Link}
                href={`/page-ui2-redirect?empId=${loggedInUserId}&area=people&subarea=PEOP_ABSENCES`}
              >
                <ListItemIcon>
                  <ArrowOutward fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('laAbsences')}</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </Box>
      }
      secondaryElement={formattedDate}
      onClick={open}
    />
  );
};
